import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import moment from 'moment';
import queryString from 'query-string';
import { Container } from 'reactstrap';

import { DOWNLOAD_LEDGERS, GET_LEDGER, GET_LEDGER_EMPTY } from '../../../../../store/actions';
import { Loader } from '../../../../Common';
import BreadcrumbTrail from '../../../../Common/BreadcrumbTrail';
import { Constant } from '../../../../Helpers/constant';
import Filter from './Filters/Filter';
import Table from './Tables/Table';
import CustomCard from '../Dashboard/Components/Card';
import totaPoints from '../../../../../assets/images/total-points.png';
import pointsRedeemed from '../../../../../assets/images/points-redeemed.png';
import unutilisedPoint from '../../../../../assets/images/unutilised-point.png';

const LedgerV2 = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Parse URL query parameters
  const { page = 1, perPageLimit = Constant.TABLE_PAGE_SIZE } = queryString.parse(location.search);

  const [currentPageNumber, setCurrentPageNumber] = useState(Number(page) - 1); // `page` is 1-based
  const [rowsPerPage, setRowsPerPage] = useState(Number(perPageLimit));
  const [currentlyAppliedFilters, setCurrentlyAppliedFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState([]);

  const { getLedger, getLedgerLoading } = useSelector((state: any) => ({
    getLedger: state?.GetLedger?.data?.data,
    getLedgerLoading: state?.GetLedger?.loading,
  }));

  useEffect(() => {
    if (getLedger !== null) {
      const totalPoints = getLedger?.total_points || 0;
      const pointsUtilised = getLedger?.points_utilised || 0;
      const pointsUnutilised = getLedger?.points_unutilised || 0;
      const expiredPoints = getLedger?.points_expired || 0;

      setCardData([
        { name: 'Total Points', count: totalPoints, icon: totaPoints },
        { name: 'Points Redeemed', count: pointsUtilised, icon: pointsRedeemed },
        { name: 'Unutilised Points', count: pointsUnutilised, icon: unutilisedPoint },
        { name: 'Expired Points', count: expiredPoints },
      ]);
    }
  }, [getLedger]);

  useEffect(() => {
    // Sync state with URL
    const { page, perPageLimit } = queryString.parse(location.search);
    setCurrentPageNumber(Number(page || 1) - 1); // Default to 1-based page
    setRowsPerPage(Number(perPageLimit || Constant.TABLE_PAGE_SIZE));
  }, [location.search]);

  useEffect(() => {
    dispatch({
      type: GET_LEDGER,
      payload: {
        data: {
          offset: currentPageNumber * rowsPerPage,
          limit: rowsPerPage,
          ...currentlyAppliedFilters,
        },
      },
    });
  }, [currentlyAppliedFilters, currentPageNumber, rowsPerPage, dispatch]);

  useEffect(() => {
    if (getLedgerLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [getLedgerLoading]);

  const updateURLParams = newParams => {
    const currentParams = queryString.parse(location.search);

    const updatedParams = {
      page: currentPageNumber + 1, // Default to current page
      perPageLimit: rowsPerPage, // Default to current rowsPerPage
      ...currentParams, // Merge existing query parameters
      ...newParams, // Override with new parameters
    };

    const newUrl = queryString.stringify(updatedParams);

    history.push({
      pathname: location.pathname,
      search: `?${newUrl}`,
    });
  };

  const filterSubmitHandler = filters => {
    setCurrentlyAppliedFilters(filters);
    dispatch({
      type: GET_LEDGER_EMPTY,
    });
    updateURLParams({ page: 1 });
  };

  const paginationHandler = pageNumber => {
    updateURLParams({ page: pageNumber + 1 });
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    updateURLParams({ page: 1, perPageLimit: newRowsPerPage });
  };

  /* ---------------------------- BREADCRUMB HANDLER ---------------------------- */
  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) {
      history.push(`/client/dashboard`);
    }
  };

  /* ---------------------------- DOWNLOAD DUMP HANDLER ---------------------------- */
  const downloadDumpHandler = () => {
    const filters = { ...currentlyAppliedFilters };

    if (!filters.start_date) {
      filters.start_date = moment().subtract(1, 'months').format('YYYY-MM-DD');
    }
    if (!filters.end_date) {
      filters.end_date = moment().format('YYYY-MM-DD');
    }

    dispatch({
      type: DOWNLOAD_LEDGERS,
      payload: {
        data: {
          ...filters,
        },
      },
    });
  };

  return (
    <>
      {loading && <Loader />}
      <div className="page-content">
        <Container fluid className="position-relative">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px 0' }}>
            <BreadcrumbTrail title="Ledger" navigationby={breadcrumNavigationHandler} navigation={[`Dashboard`, `Ledger`]} />
            <Filter filterHandler={filterSubmitHandler} />
          </div>

          <div className="scrollable-1 mt-3 mb-3">
            <div className="d-inline-flex gap-4">
              {cardData?.map((item, key) => (
                <div className="card dashboard-globe-toyota-card" key={key}>
                  <CustomCard cardData={item} />
                </div>
              ))}
            </div>
          </div>

          <Table
            data={getLedger?.ledger}
            totalCount={getLedger?.pagination?.total}
            pageNumber={currentPageNumber}
            appliedFilters={currentlyAppliedFilters}
            limit={rowsPerPage}
            handleRowsPerPageChange={handleRowsPerPageChange}
            handlePageChange={paginationHandler}
            dumpDownload={downloadDumpHandler}
          />
        </Container>
      </div>
    </>
  );
};

export default LedgerV2;
